import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Shield, Settings } from 'lucide-react';

const CONSENT_KEY = 'tb_consent_v1';

function getConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const checkConsent = useCallback(() => {
    const consent = getConsent();
    if (!consent) {
      setVisible(true);
    } else {
      setAnalytics(consent.analytics || false);
      setMarketing(consent.marketing || false);
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    checkConsent();
    const handler = () => {
      setVisible(true);
      setAnalytics(false);
      setMarketing(false);
    };
    window.addEventListener('consent-reset', handler);
    return () => window.removeEventListener('consent-reset', handler);
  }, [checkConsent]);

  const handleAccept = () => {
    setConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleReject = () => {
    setConsent({ essential: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const handleSavePrefs = () => {
    setConsent({ essential: true, analytics, marketing });
    setPrefsOpen(false);
    setVisible(false);
  };

  if (!visible && !prefsOpen) return null;

  return (
    <>
      {visible && (
        <div
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border bg-card/95 backdrop-blur-xl shadow-2xl animate-fade-up"
        >
          <div className="mx-auto max-w-7xl px-6 py-4 md:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground leading-relaxed">
                We use cookies to improve your experience. Essential cookies are always active.
                Analytics cookies help us understand site usage.{' '}
                <button
                  onClick={() => setPrefsOpen(true)}
                  className="text-primary hover:underline font-medium"
                >
                  Learn more
                </button>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                data-testid="cookie-reject"
                onClick={handleReject}
                className="text-muted-foreground hover:text-foreground"
              >
                Reject
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="cookie-preferences"
                onClick={() => setPrefsOpen(true)}
              >
                <Settings className="h-3.5 w-3.5 mr-1.5" />
                Preferences
              </Button>
              <Button
                size="sm"
                data-testid="cookie-accept"
                onClick={handleAccept}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Accept all
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Cookie Preferences</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Essential Cookies</Label>
                <p className="text-xs text-muted-foreground mt-1">Required for the site to function properly.</p>
              </div>
              <Switch checked disabled data-testid="cookie-essential-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Analytics Cookies</Label>
                <p className="text-xs text-muted-foreground mt-1">Help us understand site usage patterns.</p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid="cookie-analytics-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Marketing Cookies</Label>
                <p className="text-xs text-muted-foreground mt-1">Used for targeted content (not currently active).</p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                data-testid="cookie-marketing-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="cookie-save-preferences"
              onClick={handleSavePrefs}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
